import {
  Button,
  TextField,
  Typography,
  Box,
  InputAdornment,
  Alert,
  IconButton,
} from '@mui/material';
import { Email, LockReset, ArrowBack, Visibility, VisibilityOff, VpnKey } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import { forgotPassword, resetPasswordWithOtp } from '../api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const GS = {
  blue: '#FF8C42',
  blueLight: '#FFF5EF',
  blueDark: '#E06526',
  orange: '#FF8C42',
  orangeLight: '#F0F7FF',
  orangeDark: '#FF8C42',
  teal: '#4A7C8E',
  tealDark: '#1E3A45',
  textMuted: '#6B7280',
  border: '#E5E7EB',
  surface: '#F9FAFB',
  white: '#ffffff',
};

const VALID_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validatePassword = (password) => {
  if (!password) return 'required';
  if (password.length > 12) return 'tooLong';
  if (password.length < 8) return 'tooShort';
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[@$!%*?&#]/.test(password);
  if (!hasUpper || !hasLower || !hasNumber || !hasSpecial) return 'weak';
  return null;
};

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [loadingSend, setLoadingSend] = useState(false);
  const [loadingReset, setLoadingReset] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [attemptedSend, setAttemptedSend] = useState(false);
  const [attemptedReset, setAttemptedReset] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const emailTrimmed = email.trim();

  const emailError = useMemo(() => {
    if (!attemptedSend) return '';
    if (!emailTrimmed) return 'Email is required';
    if (!VALID_EMAIL_REGEX.test(emailTrimmed)) return 'Enter a valid email address';
    return '';
  }, [attemptedSend, emailTrimmed]);

  const passwordValidationResult = useMemo(() => validatePassword(newPassword), [newPassword]);
  const passwordError = useMemo(() => {
    // Show errors if the user has attempted a reset OR if they've started typing something invalid
    if (!attemptedReset && !newPassword) return '';
    if (passwordValidationResult === 'required') return attemptedReset ? 'Password is required' : '';
    if (passwordValidationResult === 'tooShort') return 'Password must be at least 8 characters';
    if (passwordValidationResult === 'tooLong') return 'Password must be at most 12 characters';
    if (passwordValidationResult === 'weak') return 'Must include upper, lower, number and special char (@$!%*?&#)';
    return '';
  }, [attemptedReset, newPassword, passwordValidationResult]);

  const otpError = useMemo(() => {
    if (!attemptedReset) return '';
    if (!otp.trim()) return 'Verification code is required';
    return '';
  }, [attemptedReset, otp]);

  const apiErrorMessage = (err, fallback) =>
    err.response?.data?.message || err.response?.data?.title || fallback;

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setAttemptedSend(true);
    setMessage('');
    const err =
      !emailTrimmed ? 'Email is required' : !VALID_EMAIL_REGEX.test(emailTrimmed) ? 'Enter a valid email address' : '';
    if (err) {
      setIsError(true);
      setMessage(err);
      return;
    }
    setLoadingSend(true);
    try {
      await forgotPassword(emailTrimmed);
      setIsError(false);
      setOtpSent(true);
      setOtp('');
      setNewPassword('');
      setAttemptedReset(false);
      setMessage('We sent a verification code to your email. Enter it below with your new password.');
    } catch (err) {
      setIsError(true);
      setMessage(apiErrorMessage(err, 'Could not send the verification code. Please try again.'));
    } finally {
      setLoadingSend(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setAttemptedReset(true);
    setMessage('');
    const passErr = passwordValidationResult !== null;
    const otpErr = !otp.trim() ? 'Verification code is required' : '';
    if (otpErr || passErr) {
      setIsError(true);
      setMessage(otpErr ? otpErr : 'Please enter a strong password (8-12 chars, mixed case, number, special).');
      return;
    }
    setLoadingReset(true);
    try {
      const { data } = await resetPasswordWithOtp({
        email: emailTrimmed,
        newPassword: newPassword,
        otp: otp.trim(),
      });
      setIsError(false);
      const successMsg =
        (typeof data?.message === 'string' && data.message.trim()) ||
        'Password has been reset. You can sign in with your new password.';
      toast.success(successMsg);
      logout();
      navigate('/login', {
        replace: true,
        state: { loginEmail: emailTrimmed },
      });
    } catch (err) {
      setIsError(true);
      setMessage(apiErrorMessage(err, 'Password reset failed. Check your code and try again.'));
    } finally {
      setLoadingReset(false);
    }
  };

  const goBackToEmail = () => {
    setOtpSent(false);
    setOtp('');
    setNewPassword('');
    setMessage('');
    setIsError(false);
    setAttemptedSend(false);
    setAttemptedReset(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: GS.surface,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          bgcolor: GS.white,
          borderRadius: 4,
          border: `1px solid ${GS.border}`,
          overflow: 'hidden',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}
      >
        <Box sx={{ height: 4, bgcolor: GS.blue }} />

        <Box sx={{ p: 4 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 3,
              bgcolor: GS.orangeLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <LockReset sx={{ fontSize: 28, color: GS.blue }} />
          </Box>

          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: GS.tealDark, letterSpacing: '-0.3px', mb: 0.5 }}
          >
            Forgot Password?
          </Typography>
          <Typography variant="body2" sx={{ color: GS.textMuted, mb: 3, lineHeight: 1.6 }}>
            {otpSent
              ? 'Enter the code from your email and choose a new password.'
              : 'Enter your registered email and we will send you a verification code.'}
          </Typography>

          {message && (
            <Alert
              severity={isError ? 'error' : 'success'}
              sx={{
                mb: 2.5,
                borderRadius: 2,
                fontSize: '0.8rem',
                ...(isError
                  ? {}
                  : {
                      bgcolor: GS.orangeLight,
                      color: GS.orangeDark,
                      border: `1px solid #F9C5A0`,
                      '& .MuiAlert-icon': { color: GS.orange },
                    }),
              }}
            >
              {message}
            </Alert>
          )}

          {!otpSent && (
            <Box component="form" onSubmit={handleSendOtp}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: GS.tealDark, mb: 0.5, display: 'block' }}
              >
                Email Address
              </Typography>
              <TextField
                required
                fullWidth
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (message && attemptedSend) {
                    setMessage('');
                    setIsError(false);
                  }
                }}
                type="email"
                size="small"
                error={Boolean(emailError)}
                helperText={emailError || undefined}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ fontSize: 18, color: GS.textMuted }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: GS.orange },
                    '&.Mui-focused fieldset': { borderColor: GS.orange },
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loadingSend}
                sx={{
                  py: 1.3,
                  borderRadius: 2,
                  bgcolor: GS.orange,
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: GS.orangeDark, boxShadow: 'none' },
                  '&:disabled': { bgcolor: `${GS.orange}80` },
                }}
              >
                {loadingSend ? 'Sending...' : 'Send verification code'}
              </Button>
            </Box>
          )}

          {otpSent && (
            <Box component="form" onSubmit={handleResetPassword}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: GS.tealDark, mb: 0.5, display: 'block' }}
              >
                Email
              </Typography>
              <TextField
                fullWidth
                value={email}
                disabled
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ fontSize: 18, color: GS.textMuted }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': { borderRadius: 2 },
                }}
              />

              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: GS.tealDark, mb: 0.5, display: 'block' }}
              >
                Verification code
              </Typography>
              <TextField
                required
                fullWidth
                placeholder="Enter code from email"
                value={otp}
                onChange={(e) => {
                  setOtp(e.target.value);
                  if (message) {
                    setMessage('');
                    setIsError(false);
                  }
                }}
                size="small"
                autoComplete="one-time-code"
                error={Boolean(otpError)}
                helperText={otpError || undefined}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKey sx={{ fontSize: 18, color: GS.textMuted }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: GS.orange },
                    '&.Mui-focused fieldset': { borderColor: GS.orange },
                  },
                }}
              />

              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: GS.tealDark, mb: 0.5, display: 'block' }}
              >
                New password
              </Typography>
              <TextField
                required
                fullWidth
                placeholder="New password"
                error={Boolean(passwordError)}
                helperText={passwordError || undefined}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (message) {
                    setMessage('');
                    setIsError(false);
                  }
                }}
                type={showPassword ? 'text' : 'password'}
                size="small"
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: GS.orange },
                    '&.Mui-focused fieldset': { borderColor: GS.orange },
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loadingReset}
                sx={{
                  py: 1.3,
                  borderRadius: 2,
                  bgcolor: GS.orange,
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: GS.orangeDark, boxShadow: 'none' },
                  '&:disabled': { bgcolor: `${GS.orange}80` },
                }}
              >
                {loadingReset ? 'Resetting...' : 'Reset password'}
              </Button>

              <Button
                type="button"
                fullWidth
                variant="text"
                onClick={goBackToEmail}
                disabled={loadingReset}
                sx={{
                  mt: 1,
                  textTransform: 'none',
                  color: GS.textMuted,
                  '&:hover': { bgcolor: 'transparent', color: GS.tealDark },
                }}
              >
                Use a different email
              </Button>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              mt: 3,
              cursor: 'pointer',
              color: GS.textMuted,
              '&:hover': { color: GS.tealDark },
              transition: 'color 0.15s',
            }}
            onClick={() => navigate('/login')}
          >
            <ArrowBack sx={{ fontSize: 16 }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Back to Login
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            px: 4,
            py: 2,
            bgcolor: GS.surface,
            borderTop: `1px solid ${GS.border}`,
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" sx={{ color: GS.textMuted }}>
            Settled Customer Portal · Simplify. Solve. Settle.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
